import * as THREE from 'three';
import type { ColorRepresentation } from 'three';
import type { Coord, MoveEvent, PieceType, SpecialKind } from '../logic/types';

/** A quality tier used to keep the effect budget predictable on mobile. */
export type VfxQuality = 'low' | 'medium' | 'high';

export type VfxPhaseName = 'anticipation' | 'impact' | 'dissipation';

export type VfxBeamDirection = 'horizontal' | 'vertical' | 'diagonal';

export type VfxAnchor = Coord | THREE.Vector3;

export type VfxEffectKind =
  | 'selection'
  | 'swap'
  | 'invalid-swap'
  | 'anticipation'
  | 'clear'
  | 'spawn'
  | 'cascade'
  | 'special-creation'
  | 'beam'
  | 'nova'
  | 'burst'
  | 'prism'
  | 'combo'
  | 'blocker-damage'
  | 'objective'
  | 'hazard'
  | 'reshuffle'
  | 'win'
  | 'loss';

export interface VfxPhase {
  name: VfxPhaseName;
  /** Progress through the complete effect, from 0 to 1. */
  normalized: number;
  /** Progress through the current phase, from 0 to 1. */
  progress: number;
  /** A smooth progress value appropriate for the current phase. */
  eased: number;
}

export interface VfxDirectorOptions {
  /** The scene or group that receives the director's single root object. */
  scene?: THREE.Object3D;
  /** World location of cell (0, 0) when using the default mapper. */
  origin?: THREE.Vector3;
  /** Distance between logical cell centers for the default mapper. */
  cellSize?: number;
  /** Board-space right and down vectors for the default mapper. */
  boardRight?: THREE.Vector3;
  boardDown?: THREE.Vector3;
  /** Normal of the board surface. The default board lies in the XZ plane. */
  boardNormal?: THREE.Vector3;
  /** Override coordinate mapping without coupling VFX to a renderer or camera. */
  coordinateToWorld?: (coord: Coord, target: THREE.Vector3) => THREE.Vector3 | void;
  quality?: VfxQuality;
  reducedMotion?: boolean;
  enabled?: boolean;
  /** Maximum number of simultaneous effect roots. */
  maxEffects?: number;
  /** Maximum number of live particles across all effects. */
  particleBudget?: number;
  randomSeed?: number;
  /** Colors are only a fallback; callers can always provide an event color. */
  pieceColors?: Partial<Record<PieceType, ColorRepresentation>>;
}

export interface VfxColorOptions {
  color?: ColorRepresentation;
  pieceType?: PieceType;
  intensity?: number;
  duration?: number;
}

export interface ClearVfxOptions extends VfxColorOptions {
  special?: SpecialKind;
  cascade?: number;
  particles?: boolean;
}

export interface CascadeVfxOptions extends VfxColorOptions {
  label?: string;
}

export interface BeamVfxOptions extends VfxColorOptions {
  direction?: VfxBeamDirection;
  cells?: readonly Coord[];
  origin?: VfxAnchor;
}

export interface NovaVfxOptions extends VfxColorOptions {
  radius?: number;
  cells?: readonly Coord[];
}

export interface BurstVfxOptions extends VfxColorOptions {
  radius?: number;
  particles?: boolean;
}

export interface PrismVfxOptions extends VfxColorOptions {
  targets?: readonly Coord[];
}

export interface ComboVfxOptions extends VfxColorOptions {
  cells?: readonly Coord[];
  label?: string;
}

export interface BlockerDamageVfxOptions extends VfxColorOptions {
  kind?: string;
  amount?: number;
}

export interface ObjectiveVfxOptions extends VfxColorOptions {
  label?: string;
  center?: VfxAnchor;
}

export interface ReshuffleVfxOptions extends VfxColorOptions {
  center?: VfxAnchor;
}

export interface WinVfxOptions extends VfxColorOptions {
  center?: VfxAnchor;
  cells?: readonly Coord[];
}

export interface LossVfxOptions extends VfxColorOptions {
  center?: VfxAnchor;
  cells?: readonly Coord[];
}

interface EffectSlot {
  root: THREE.Group;
  id: number;
  kind: VfxEffectKind;
  active: boolean;
  persistent: boolean;
  age: number;
  duration: number;
  startedAt: number;
  update?: EffectUpdater;
  particles: ParticleField[];
  materials: THREE.Material[];
}

type EffectUpdater = (slot: EffectSlot, phase: VfxPhase, deltaSeconds: number) => void;

interface ParticleField {
  points: THREE.Points;
  geometry: THREE.BufferGeometry;
  positionAttribute: THREE.BufferAttribute;
  colorAttribute: THREE.BufferAttribute;
  positions: Float32Array;
  velocities: Float32Array;
  count: number;
  age: number;
  duration: number;
  gravity: THREE.Vector3;
  drag: number;
  baseOpacity: number;
  material?: THREE.PointsMaterial;
}

interface BeamSegment {
  mesh: THREE.Mesh;
  from: THREE.Vector3;
  to: THREE.Vector3;
  radius: number;
}

const TAU = Math.PI * 2;
const EPSILON = 0.00001;

const DEFAULT_PIECE_COLORS: readonly ColorRepresentation[] = [
  '#ff6a67',
  '#6dd9ee',
  '#ffd36a',
  '#c493ff',
  '#ff9b71',
  '#7195ff',
  '#69e0b5',
];

const SPECIAL_COLORS: Record<SpecialKind, ColorRepresentation> = {
  none: '#ffe8a6',
  beamH: '#ffd36a',
  beamV: '#63ddff',
  nova: '#ff8eae',
  prism: '#e7a5ff',
  burst: '#ffae70',
};

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

function smoothstep(value: number): number {
  const t = clamp01(value);
  return t * t * (3 - 2 * t);
}

function easeOutCubic(value: number): number {
  const t = 1 - clamp01(value);
  return 1 - t * t * t;
}

function easeInOut(value: number): number {
  const t = clamp01(value);
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function lerp(minimum: number, maximum: number, amount: number): number {
  return minimum + (maximum - minimum) * amount;
}

function phaseAt(normalized: number, anticipation = 0.28, impact = 0.18): VfxPhase {
  const t = clamp01(normalized);
  const anticipationEnd = clamp(anticipation, 0.05, 0.75);
  const impactEnd = clamp(anticipationEnd + impact, anticipationEnd + 0.05, 0.94);

  if (t < anticipationEnd) {
    const progress = t / anticipationEnd;
    return {
      name: 'anticipation',
      normalized: t,
      progress,
      eased: smoothstep(progress),
    };
  }

  if (t < impactEnd) {
    const progress = (t - anticipationEnd) / (impactEnd - anticipationEnd);
    return {
      name: 'impact',
      normalized: t,
      progress,
      eased: easeOutCubic(progress),
    };
  }

  const progress = (t - impactEnd) / (1 - impactEnd);
  return {
    name: 'dissipation',
    normalized: t,
    progress,
    eased: easeInOut(progress),
  };
}

function isFiniteVector(vector: THREE.Vector3): boolean {
  return Number.isFinite(vector.x) && Number.isFinite(vector.y) && Number.isFinite(vector.z);
}

/**
 * A renderer-independent effect director.
 *
 * The director owns one scene root and only advances when `update` is called by
 * the host game loop. It never reads a renderer, camera, DOM element, or board
 * mesh. The logical board remains the source of truth; this class only consumes
 * coordinates and visual events.
 */
export class VfxDirector {
  public readonly root: THREE.Group;

  private readonly effectLayer: THREE.Group;
  private readonly origin: THREE.Vector3;
  private readonly boardRight: THREE.Vector3;
  private readonly boardDown: THREE.Vector3;
  private readonly boardNormal: THREE.Vector3;
  private readonly planeQuaternion: THREE.Quaternion;
  private readonly coordinateToWorld?: VfxDirectorOptions['coordinateToWorld'];
  private readonly cellSize: number;
  private readonly quality: VfxQuality;
  private readonly maxEffects: number;
  private readonly particleBudget: number;
  private readonly particleCapacity: number;
  private readonly pieceColors: Partial<Record<PieceType, ColorRepresentation>>;
  private readonly slots: EffectSlot[] = [];
  private readonly selectionSlots = new Map<string, EffectSlot>();
  private readonly particlePool: ParticleField[] = [];
  private readonly geometryCache = new Map<string, THREE.BufferGeometry>();
  private readonly materialPool = new Map<string, THREE.Material[]>();
  private readonly temporary = {
    point: new THREE.Vector3(),
    pointB: new THREE.Vector3(),
    pointC: new THREE.Vector3(),
    direction: new THREE.Vector3(),
    midpoint: new THREE.Vector3(),
    sum: new THREE.Vector3(),
  };

  private randomState: number;
  private sequence = 0;
  private time = 0;
  private activeParticleCount = 0;
  private reducedMotion: boolean;
  private enabled: boolean;
  private disposed = false;

  public constructor(scene?: THREE.Object3D, options?: VfxDirectorOptions);
  public constructor(options?: VfxDirectorOptions);
  public constructor(
    sceneOrOptions?: THREE.Object3D | VfxDirectorOptions,
    providedOptions: VfxDirectorOptions = {},
  ) {
    const looksLikeScene = Boolean(
      sceneOrOptions
      && typeof (sceneOrOptions as THREE.Object3D).add === 'function'
      && typeof (sceneOrOptions as THREE.Object3D).remove === 'function',
    );
    const scene = looksLikeScene ? sceneOrOptions as THREE.Object3D : undefined;
    const options = looksLikeScene
      ? providedOptions
      : (sceneOrOptions as VfxDirectorOptions | undefined) ?? providedOptions;

    this.origin = (options.origin ?? new THREE.Vector3()).clone();
    this.boardRight = this.normalizedOr(options.boardRight, new THREE.Vector3(1, 0, 0));
    this.boardDown = this.normalizedOr(options.boardDown, new THREE.Vector3(0, 0, 1));
    this.boardNormal = this.normalizedOr(options.boardNormal, new THREE.Vector3(0, 1, 0));
    this.cellSize = Math.max(0.001, options.cellSize ?? 1);
    this.coordinateToWorld = options.coordinateToWorld;
    this.quality = options.quality ?? 'high';
    this.maxEffects = Math.max(12, Math.floor(options.maxEffects ?? 96));
    this.particleBudget = Math.max(0, Math.floor(options.particleBudget ?? this.qualityParticleBudget()));
    this.particleCapacity = this.quality === 'low' ? 24 : this.quality === 'medium' ? 36 : 48;
    this.pieceColors = { ...options.pieceColors };
    this.reducedMotion = options.reducedMotion ?? false;
    this.enabled = options.enabled ?? true;
    this.randomState = (options.randomSeed ?? 0x4d595df4) >>> 0;
    if (this.randomState === 0) this.randomState = 0x6d2b79f5;

    this.planeQuaternion = new THREE.Quaternion().setFromUnitVectors(
      new THREE.Vector3(0, 0, 1),
      this.boardNormal,
    );
    this.root = new THREE.Group();
    this.root.name = 'vfx-director';
    this.root.matrixAutoUpdate = true;
    this.effectLayer = new THREE.Group();
    this.effectLayer.name = 'vfx-effects';
    this.root.add(this.effectLayer);

    const targetScene = scene ?? options.scene;
    if (targetScene) this.attach(targetScene);
  }

  /** Attach the single VFX root to a scene or any Object3D layer. */
  public attach(scene: THREE.Object3D): this {
    if (this.disposed) return this;
    if (this.root.parent !== scene) {
      this.root.parent?.remove(this.root);
      scene.add(this.root);
    }
    return this;
  }

  public attachTo(scene: THREE.Object3D): this {
    return this.attach(scene);
  }

  /** Detach without disposing pooled resources, useful when changing scenes. */
  public detach(): this {
    this.root.parent?.remove(this.root);
    return this;
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.root.visible = enabled;
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  public setReducedMotion(reducedMotion: boolean): void {
    this.reducedMotion = reducedMotion;
  }

  public isReducedMotion(): boolean {
    return this.reducedMotion;
  }

  public get activeEffectCount(): number {
    let count = 0;
    for (const slot of this.slots) if (slot.active) count += 1;
    return count;
  }

  public get liveParticleCount(): number {
    return this.activeParticleCount;
  }

  public getActiveEffectCount(): number {
    return this.activeEffectCount;
  }

  public getLiveParticleCount(): number {
    return this.liveParticleCount;
  }

  /**
   * Advance all effect timelines. Hosts should call this once per frame with
   * seconds, not milliseconds. Large background-tab deltas are bounded so a
   * resumed page cannot tunnel through an effect or create a particle spike.
   */
  public update(deltaSeconds: number): void {
    if (this.disposed || !this.enabled) return;
    const delta = clamp(Number.isFinite(deltaSeconds) ? deltaSeconds : 0, 0, 0.1);
    if (delta <= 0) return;
    this.time += delta;

    for (const slot of this.slots) {
      if (!slot.active) continue;
      slot.age += delta;
      const normalized = slot.duration === Infinity ? 0 : clamp01(slot.age / slot.duration);
      const phase = phaseAt(normalized);
      slot.update?.(slot, phase, delta);
      this.updateParticles(slot, delta);
      if (slot.duration !== Infinity && slot.age >= slot.duration) this.release(slot);
    }
  }

  /** Stop all effects, including persistent selection highlights. */
  public clearAll(): void {
    for (const slot of this.slots) {
      if (slot.active) this.release(slot);
    }
    this.selectionSlots.clear();
  }

  /** Alias useful to hosts that treat the director like a transient layer. */
  public reset(): void {
    this.clearAll();
  }

  /** Show or remove a persistent, readable selected-cell treatment. */
  public selection(coord: Coord, selected = true, options: VfxColorOptions = {}): void {
    const key = this.coordKey(coord);
    if (!selected) {
      this.clearSelection(coord);
      return;
    }
    if (!this.enabled || this.disposed || this.selectionSlots.has(key)) return;

    const color = this.resolveColor(options.color, options.pieceType, '#ffe8a6');
    const slot = this.beginEffect('selection', Infinity, (activeSlot) => {
      activeSlot.persistent = true;
      activeSlot.root.position.copy(this.worldPoint(coord, this.temporary.point));
      activeSlot.root.quaternion.copy(this.planeQuaternion);

      const haloMaterial = this.takeMeshMaterial(activeSlot, color, 0.1, true);
      const ringMaterial = this.takeMeshMaterial(activeSlot, color, 0.84, true);
      const glintMaterial = this.takeMeshMaterial(activeSlot, '#fff4c2', 0.95, true);
      const halo = this.addMesh(activeSlot, 'selection-halo', haloMaterial);
      const ring = this.addMesh(activeSlot, 'selection-ring', ringMaterial);
      const glints: THREE.Mesh[] = [];
      for (let index = 0; index < 4; index += 1) {
        const angle = index * (TAU / 4) + Math.PI / 4;
        const glint = this.addMesh(activeSlot, 'diamond', glintMaterial);
        glint.position.set(Math.cos(angle) * 0.56, Math.sin(angle) * 0.56, 0.02);
        glint.scale.setScalar(0.42);
        glints.push(glint);
      }

      return (effectSlot, _phase, delta) => {
        const pulse = this.reducedMotion ? 0 : Math.sin(this.time * 4.2) * 0.05;
        const haloScale = 1 + pulse + (this.reducedMotion ? 0 : Math.sin(this.time * 2.1) * 0.03);
        halo.scale.setScalar(haloScale);
        ring.scale.setScalar(1 + pulse * 0.65);
        if (!this.reducedMotion) ring.rotation.z += delta * 0.65;
        for (let index = 0; index < glints.length; index += 1) {
          const glint = glints[index];
          const angle = index * (TAU / 4) + Math.PI / 4 + (this.reducedMotion ? 0 : this.time * 0.45);
          glint.position.x = Math.cos(angle) * (0.56 + pulse);
          glint.position.y = Math.sin(angle) * (0.56 + pulse);
        }
        this.setMaterialOpacity(effectSlot, haloMaterial, 0.08 + Math.max(0, pulse) * 0.5);
        this.setMaterialOpacity(effectSlot, ringMaterial, 0.72 + Math.max(0, pulse) * 1.1);
      };
    });

    if (slot) this.selectionSlots.set(key, slot);
  }

  public select(coord: Coord, selected = true, options: VfxColorOptions = {}): void {
    this.selection(coord, selected, options);
  }

  public showSelection(coord: Coord, options: VfxColorOptions = {}): void {
    this.selection(coord, true, options);
  }

  public clearSelection(coord?: Coord): void {
    if (coord) {
      const slot = this.selectionSlots.get(this.coordKey(coord));
      if (slot) this.release(slot);
      return;
    }
    for (const slot of this.selectionSlots.values()) this.release(slot);
    this.selectionSlots.clear();
  }

  /** A quick, non-destructive trail used for a successful swap. */
  public swap(from: Coord, to: Coord, options: VfxColorOptions = {}): void {
    const start = this.worldPoint(from, this.temporary.point).clone().addScaledVector(this.boardNormal, 0.08);
    const end = this.worldPoint(to, this.temporary.pointB).clone().addScaledVector(this.boardNormal, 0.08);
    const color = this.resolveColor(options.color, options.pieceType, '#b8f7ff');

    this.beginEffect('swap', options.duration ?? 0.3, (slot) => {
      const outerMaterial = this.takeMeshMaterial(slot, color, 0.45, true);
      const coreMaterial = this.takeMeshMaterial(slot, '#ffffff', 0.8, true);
      const outer = this.addCylinderBetween(slot, start, end, 0.075, outerMaterial);
      const core = this.addCylinderBetween(slot, start, end, 0.024, coreMaterial);
      const pulse = this.addMesh(slot, 'swap-pulse', coreMaterial);
      pulse.position.copy(start);
      pulse.scale.setScalar(0.65);

      return (activeSlot, phase, delta) => {
        const travel = phase.name === 'anticipation'
          ? phase.eased * 0.3
          : phase.name === 'impact'
            ? 0.3 + phase.eased * 0.7
            : 1;
        pulse.position.lerpVectors(start, end, travel);
        const width = phase.name === 'impact' ? lerp(1, 1.35, phase.eased) : 1;
        outer.scale.x = 0.075 * width;
        outer.scale.z = 0.075 * width;
        core.scale.x = 0.024 * width;
        core.scale.z = 0.024 * width;
        const opacity = phase.name === 'dissipation' ? 1 - phase.eased : 1;
        this.setMaterialOpacity(activeSlot, outerMaterial, 0.4 * opacity);
        this.setMaterialOpacity(activeSlot, coreMaterial, 0.8 * opacity);
      };
    });
  }

  /** Feedback for a rejected adjacent swap. */
  public invalidSwap(from: Coord, to: Coord, options: VfxColorOptions = {}): void {
    const start = this.worldPoint(from, this.temporary.point).clone().addScaledVector(this.boardNormal, 0.1);
    const end = this.worldPoint(to, this.temporary.pointB).clone().addScaledVector(this.boardNormal, 0.1);
    const color = this.resolveColor(options.color, options.pieceType, '#ff667b');

    this.beginEffect('invalid-swap', options.duration ?? 0.42, (slot) => {
      const ringMaterial = this.takeMeshMaterial(slot, color, 0.75, true);
      const lineMaterial = this.takeMeshMaterial(slot, '#ffb1a1', 0.68, true);
      const flashMaterial = this.takeMeshMaterial(slot, '#fff0d4', 0.78, true);
      const rings = [
        this.addPlaneMesh(slot, 'invalid-ring', start, ringMaterial),
        this.addPlaneMesh(slot, 'invalid-ring', end, ringMaterial),
      ];
      const connector = this.addCylinderBetween(slot, start, end, 0.025, lineMaterial);
      const marker = this.addMesh(slot, 'diamond', flashMaterial);
      marker.position.copy(this.temporary.midpoint.copy(start).add(end).multiplyScalar(0.5));
      marker.quaternion.copy(this.planeQuaternion);
      marker.scale.setScalar(0.33);

      return (activeSlot, phase) => {
        const wobble = this.reducedMotion ? 0 : Math.sin(this.time * 34) * 0.06;
        const scale = phase.name === 'anticipation'
          ? lerp(0.55, 0.9, phase.eased)
          : phase.name === 'impact'
            ? lerp(0.9, 1.15, phase.eased)
            : lerp(1.15, 1.5, phase.eased);
        for (let index = 0; index < rings.length; index += 1) {
          const ring = rings[index];
          ring.scale.setScalar(scale);
          ring.rotation.z = (index === 0 ? 1 : -1) * wobble;
        }
        marker.rotation.z = this.reducedMotion ? 0 : wobble * 2;
        const opacity = phase.name === 'dissipation' ? 1 - phase.eased : 1;
        this.setMaterialOpacity(activeSlot, ringMaterial, 0.75 * opacity);
        this.setMaterialOpacity(activeSlot, lineMaterial, 0.68 * opacity);
        this.setMaterialOpacity(activeSlot, flashMaterial, 0.78 * opacity);
        connector.visible = opacity > 0.01;
      };
    });
  }

  public swapRejected(from: Coord, to: Coord, options: VfxColorOptions = {}): void {
    this.invalidSwap(from, to, options);
  }

  /** A restrained pre-clear pulse that lets the player read what is about to happen. */
  public anticipateMatch(cells: readonly Coord[], options: VfxColorOptions = {}): void {
    const visualCells = this.limitCells(this.uniqueCells(cells), this.cellLimit(32));
    if (visualCells.length === 0) return;
    const color = this.resolveColor(options.color, options.pieceType, '#fff0a4');

    this.beginEffect('anticipation', options.duration ?? 0.52, (slot) => {
      const ringMaterial = this.takeMeshMaterial(slot, color, 0.72, true);
      const coreMaterial = this.takeMeshMaterial(slot, '#fff9e7', 0.3, true);
      const rings: THREE.Mesh[] = [];
      const cores: THREE.Mesh[] = [];
      for (const cell of visualCells) {
        const point = this.worldPoint(cell, this.temporary.point).clone().addScaledVector(this.boardNormal, 0.07);
        rings.push(this.addPlaneMesh(slot, 'anticipation-ring', point, ringMaterial));
        cores.push(this.addPlaneMesh(slot, 'anticipation-core', point, coreMaterial));
      }

      return (activeSlot, phase) => {
        const scale = phase.name === 'anticipation'
          ? lerp(0.68, 1.02, phase.eased)
          : phase.name === 'impact'
            ? lerp(1.02, 1.18, phase.eased)
            : lerp(1.18, 1.45, phase.eased);
        for (let index = 0; index < rings.length; index += 1) {
          const ring = rings[index];
          ring.scale.setScalar(scale);
          ring.rotation.z = this.reducedMotion ? 0 : (index % 2 === 0 ? 1 : -1) * this.time * 0.8;
          cores[index].scale.setScalar(phase.name === 'dissipation' ? 1 - phase.eased : phase.eased * 0.75 + 0.25);
        }
        const opacity = phase.name === 'dissipation' ? 0.72 * (1 - phase.eased) : 0.72;
        this.setMaterialOpacity(activeSlot, ringMaterial, opacity);
        this.setMaterialOpacity(activeSlot, coreMaterial, phase.name === 'impact' ? 0.58 : 0.28);
      };
    });
  }

  public matchAnticipation(cells: readonly Coord[], options: VfxColorOptions = {}): void {
    this.anticipateMatch(cells, options);
  }

  /** Clear matched cells with a readable anticipation, impact, and fade-out. */
  public clear(cells: readonly Coord[], options: ClearVfxOptions = {}): void {
    const unique = this.uniqueCells(cells);
    const visualCells = this.limitCells(unique, this.cellLimit(36));
    if (visualCells.length === 0) return;
    const color = this.resolveColor(options.color, options.pieceType, '#ffe6a1');
    const intensity = clamp(options.intensity ?? 1, 0.5, 2.5);
    const origins = visualCells.map((cell) => this.worldPoint(cell, new THREE.Vector3()).addScaledVector(this.boardNormal, 0.1));

    this.beginEffect('clear', options.duration ?? (0.62 + Math.min(0.08, visualCells.length * 0.005)), (slot) => {
      const ringMaterial = this.takeMeshMaterial(slot, color, 0.9, true);
      const coreMaterial = this.takeMeshMaterial(slot, '#fffdf0', 0.82, true);
      const shardMaterial = this.takeMeshMaterial(slot, color, 0.8, true);
      const rings: THREE.Mesh[] = [];
      const cores: THREE.Mesh[] = [];
      const shards: THREE.Mesh[] = [];
      for (const origin of origins) {
        rings.push(this.addPlaneMesh(slot, 'clear-ring', origin, ringMaterial));
        cores.push(this.addPlaneMesh(slot, 'clear-core', origin, coreMaterial));
        if (visualCells.length <= 10) {
          const shard = this.addMesh(slot, 'diamond', shardMaterial);
          shard.position.copy(origin);
          shard.scale.setScalar(0.16 * intensity);
          shards.push(shard);
        }
      }

      if (options.particles !== false) {
        this.emitParticles(slot, origins, 8 + visualCells.length * 2, color, 0.25, 0.72, 0.62, -0.7);
      }

      return (activeSlot, phase) => {
        const expansion = phase.name === 'anticipation'
          ? lerp(0.32, 0.72, phase.eased)
          : phase.name === 'impact'
            ? lerp(0.72, 1.14, phase.eased)
            : lerp(1.14, 1.65, phase.eased);
        const coreScale = phase.name === 'anticipation'
          ? lerp(0.25, 0.82, phase.eased)
          : phase.name === 'impact'
            ? lerp(0.82, 1.1, phase.eased)
            : lerp(1.1, 0.05, phase.eased);
        for (let index = 0; index < rings.length; index += 1) {
          rings[index].scale.setScalar(expansion * intensity);
          cores[index].scale.setScalar(coreScale * intensity);
          rings[index].rotation.z = this.reducedMotion ? 0 : (index % 2 === 0 ? 1 : -1) * this.time * 2.2;
        }
        for (let index = 0; index < shards.length; index += 1) {
          const shard = shards[index];
          const angle = index * 1.7 + this.time * (this.reducedMotion ? 0 : 4.2);
          shard.position.copy(origins[index % origins.length]);
          shard.position.addScaledVector(this.boardRight, Math.cos(angle) * expansion * 0.35);
          shard.position.addScaledVector(this.boardDown, Math.sin(angle) * expansion * 0.35);
          shard.scale.setScalar((phase.name === 'dissipation' ? 1 - phase.eased : 1) * 0.16 * intensity);
        }
        const fade = phase.name === 'dissipation' ? 1 - phase.eased : 1;
        this.setMaterialOpacity(activeSlot, ringMaterial, 0.9 * fade);
        this.setMaterialOpacity(activeSlot, coreMaterial, 0.82 * fade);
        this.setMaterialOpacity(activeSlot, shardMaterial, 0.8 * fade);
      };
    });
  }

  public matchClear(cells: readonly Coord[], options: ClearVfxOptions = {}): void {
    this.clear(cells, options);
  }

  /** Small upward arrival streaks for pieces entering from a spawn edge. */
  public spawn(cells: readonly Coord[], options: VfxColorOptions = {}): void {
    const visualCells = this.limitCells(this.uniqueCells(cells), this.cellLimit(24));
    if (visualCells.length === 0) return;
    const color = this.resolveColor(options.color, options.pieceType, '#b7f7df');
    const origins = visualCells.map((cell) => this.worldPoint(cell, new THREE.Vector3()).addScaledVector(this.boardNormal, -0.25));

    this.beginEffect('spawn', options.duration ?? 0.42, (slot) => {
      const trailMaterial = this.takeMeshMaterial(slot, color, 0.54, true);
      const coreMaterial = this.takeMeshMaterial(slot, '#ffffff', 0.62, true);
      const trails: THREE.Mesh[] = [];
      const cores: THREE.Mesh[] = [];
      for (const origin of origins) {
        const end = origin.clone().addScaledVector(this.boardNormal, 0.38);
        trails.push(this.addCylinderBetween(slot, origin, end, 0.035, trailMaterial));
        const core = this.addMesh(slot, 'spawn-core', coreMaterial);
        core.position.copy(origin);
        core.scale.setScalar(0.24);
        cores.push(core);
      }
      this.emitParticles(slot, origins, 4 + visualCells.length, color, 0.12, 0.42, 0.4, 0.3);

      return (activeSlot, phase) => {
        const progress = phase.name === 'anticipation'
          ? phase.eased * 0.25
          : phase.name === 'impact'
            ? 0.25 + phase.eased * 0.75
            : 1;
        for (let index = 0; index < cores.length; index += 1) {
          const core = cores[index];
          core.position.copy(origins[index]).addScaledVector(this.boardNormal, progress * 0.38);
          core.scale.setScalar(lerp(0.16, 0.28, phase.name === 'dissipation' ? 1 - phase.eased : phase.eased));
          trails[index].scale.x = lerp(0.45, 1, progress);
          trails[index].scale.z = lerp(0.45, 1, progress);
        }
        const fade = phase.name === 'dissipation' ? 1 - phase.eased : 1;
        this.setMaterialOpacity(activeSlot, trailMaterial, 0.54 * fade);
        this.setMaterialOpacity(activeSlot, coreMaterial, 0.62 * fade);
      };
    });
  }

  /** A visible cue for the start of a cascade; intensity rises with the chain. */
  public cascade(
    cells: readonly Coord[],
    cascadeIndex?: number,
    options?: CascadeVfxOptions,
  ): void;
  public cascade(
    cascadeIndex: number,
    cells?: readonly Coord[],
    options?: CascadeVfxOptions,
  ): void;
  public cascade(
    cellsOrIndex: readonly Coord[] | number,
    indexOrCells: number | readonly Coord[] = 1,
    options: CascadeVfxOptions = {},
  ): void {
    const cells = typeof cellsOrIndex === 'number'
      ? (Array.isArray(indexOrCells) ? indexOrCells : [])
      : cellsOrIndex;
    const cascadeIndex = typeof cellsOrIndex === 'number'
      ? cellsOrIndex
      : (typeof indexOrCells === 'number' ? indexOrCells : 1);
    const visualCells = this.limitCells(this.uniqueCells(cells), this.cellLimit(24));
    const center = this.averageWorld(visualCells, this.temporary.point).clone().addScaledVector(this.boardNormal, 0.12);
    const level = clamp(Math.floor(cascadeIndex), 1, 12);
    const color = this.resolveColor(options.color, options.pieceType, level >= 4 ? '#ffd36a' : '#8beed3');
    const intensity = clamp(options.intensity ?? (1 + level * 0.08), 0.7, 2.2);
    const origins = visualCells.length > 0
      ? visualCells.map((cell) => this.worldPoint(cell, new THREE.Vector3()).addScaledVector(this.boardNormal, 0.1))
      : [center];

    this.beginEffect('cascade', options.duration ?? (0.58 + Math.min(0.12, level * 0.01)), (slot) => {
      const ringMaterial = this.takeMeshMaterial(slot, color, 0.84, true);
      const coreMaterial = this.takeMeshMaterial(slot, '#fff4c4', 0.78, true);
      const markerMaterial = this.takeMeshMaterial(slot, color, 0.78, true);
      const mainRing = this.addPlaneMesh(slot, 'cascade-ring', center, ringMaterial);
      const innerRing = this.addPlaneMesh(slot, 'cascade-ring-inner', center, ringMaterial);
      const core = this.addMesh(slot, 'cascade-core', coreMaterial);
      core.position.copy(center);
      core.scale.setScalar(0.3);
      const markers: THREE.Mesh[] = [];
      const markerCount = Math.min(8, 3 + level);
      for (let markerIndex = 0; markerIndex < markerCount; markerIndex += 1) {
        const marker = this.addMesh(slot, 'diamond', markerMaterial);
        marker.scale.setScalar(0.18 + level * 0.01);
        markers.push(marker);
      }
      this.emitParticles(slot, origins, 5 + level * 2, color, 0.24, 0.62 + level * 0.04, 0.65, -0.35);

      return (activeSlot, phase, delta) => {
        const radius = phase.name === 'anticipation'
          ? lerp(0.3, 0.7, phase.eased)
          : phase.name === 'impact'
            ? lerp(0.7, 1.28, phase.eased)
            : lerp(1.28, 1.85 + level * 0.04, phase.eased);
        mainRing.scale.setScalar(radius * intensity);
        innerRing.scale.setScalar(radius * 0.65 * intensity);
        core.scale.setScalar((phase.name === 'dissipation' ? 1 - phase.eased : lerp(0.65, 1.2, phase.eased)) * intensity * 0.34);
        if (!this.reducedMotion) {
          mainRing.rotation.z += delta * 2.7 * level;
          innerRing.rotation.z -= delta * 4.2 * level;
        }
        for (let markerIndex = 0; markerIndex < markers.length; markerIndex += 1) {
          const angle = markerIndex * (TAU / markers.length) + (this.reducedMotion ? 0 : this.time * (0.8 + level * 0.08));
          const orbit = radius * (0.48 + level * 0.012);
          markers[markerIndex].position.copy(center);
          markers[markerIndex].position.addScaledVector(this.boardRight, Math.cos(angle) * orbit);
          markers[markerIndex].position.addScaledVector(this.boardDown, Math.sin(angle) * orbit);
          markers[markerIndex].position.addScaledVector(this.boardNormal, 0.04);
          markers[markerIndex].rotation.z = angle;
          markers[markerIndex].scale.setScalar((phase.name === 'dissipation' ? 1 - phase.eased : 1) * (0.18 + level * 0.01));
        }
        const fade = phase.name === 'dissipation' ? 1 - phase.eased : 1;
        this.setMaterialOpacity(activeSlot, ringMaterial, 0.84 * fade);
        this.setMaterialOpacity(activeSlot, coreMaterial, 0.78 * fade);
        this.setMaterialOpacity(activeSlot, markerMaterial, 0.78 * fade);
      };
    });
  }

  public cascadePulse(cascadeIndex: number, cells: readonly Coord[] = [], options: CascadeVfxOptions = {}): void {
    this.cascade(cascadeIndex, cells, options);
  }

  /** A short-lived sculptural birth effect for a newly created special piece. */
  public specialCreation(coord: Coord, special: SpecialKind, options: VfxColorOptions = {}): void {
    const kind = special === 'none' ? 'burst' : special;
    const color = this.resolveColor(options.color, options.pieceType, SPECIAL_COLORS[kind]);
    const center = this.worldPoint(coord, this.temporary.point).clone().addScaledVector(this.boardNormal, 0.16);

    this.beginEffect('special-creation', options.duration ?? 0.78, (slot) => {
      slot.root.position.copy(center);
      slot.root.quaternion.copy(this.planeQuaternion);
      const ringMaterial = this.takeMeshMaterial(slot, color, 0.88, true);
      const coreMaterial = this.takeMeshMaterial(slot, '#fff9de', 0.92, true);
      const accentMaterial = this.takeMeshMaterial(slot, this.specialAccent(kind), 0.82, true);
      const ring = this.addMesh(slot, 'special-ring', ringMaterial);
      const innerRing = this.addMesh(slot, 'special-ring-inner', accentMaterial);
      const core = this.addMesh(slot, 'special-core', coreMaterial);
      core.scale.setScalar(0.18);
      const orbiters: THREE.Mesh[] = [];
      for (let index = 0; index < 4; index += 1) {
        const orbiter = this.addMesh(slot, 'special-orbiter', accentMaterial);
        orbiter.scale.setScalar(0.16);
        orbiters.push(orbiter);
      }
      const glyphs: THREE.Mesh[] = [];
      if (kind === 'beamH' || kind === 'beamV') {
        const glyph = this.addMesh(slot, 'special-beam-glyph', accentMaterial);
        glyph.scale.set(kind === 'beamH' ? 1.1 : 0.12, kind === 'beamV' ? 1.1 : 0.12, 1);
        if (kind === 'beamV') glyph.rotation.z = Math.PI / 2;
        glyphs.push(glyph);
      } else if (kind === 'prism') {
        for (let index = 0; index < 3; index += 1) {
          const glyph = this.addMesh(slot, 'special-prism-ray', accentMaterial);
          glyph.rotation.z = (index - 1) * (Math.PI / 4);
          glyph.scale.setScalar(0.65);
          glyphs.push(glyph);
        }
      } else {
        const glyph = this.addMesh(slot, kind === 'nova' ? 'special-nova-glyph' : 'special-burst-glyph', accentMaterial);
        glyph.scale.setScalar(kind === 'nova' ? 0.72 : 0.56);
        glyphs.push(glyph);
      }
      this.emitParticles(slot, [center], kind === 'prism' ? 14 : 9, color, 0.22, 0.58, 0.72, -0.25);

      const rotationRate = kind === 'prism' ? 1.35 : kind === 'nova' ? 1.1 : kind === 'burst' ? 1.5 : 1;
      return (activeSlot, phase, delta) => {
        const scale = phase.name === 'anticipation'
          ? lerp(0.52, 0.96, phase.eased)
          : phase.name === 'impact'
            ? lerp(0.96, 1.24, phase.eased)
            : lerp(1.24, 1.78, phase.eased);
        ring.scale.setScalar(scale);
        innerRing.scale.setScalar(scale * 0.68);
        core.scale.setScalar((phase.name === 'dissipation' ? 1 - phase.eased : lerp(0.7, 1.15, phase.eased)) * 0.2);
        if (!this.reducedMotion) {
          ring.rotation.z += delta * rotationRate * 3;
          innerRing.rotation.z -= delta * rotationRate * 4.6;
          core.rotation.x += delta * 2.4;
          core.rotation.z += delta * 3.3;
        }
        for (let index = 0; index < orbiters.length; index += 1) {
          const angle = index * (TAU / orbiters.length) + (this.reducedMotion ? 0 : this.time * 1.8);
          const orbit = scale * 0.6;
          orbiters[index].position.set(Math.cos(angle) * orbit, Math.sin(angle) * orbit, 0.06);
          orbiters[index].rotation.z = angle;
          orbiters[index].scale.setScalar((phase.name === 'dissipation' ? 1 - phase.eased : 1) * 0.16);
        }
        const fade = phase.name === 'dissipation' ? 1 - phase.eased : 1;
        this.setMaterialOpacity(activeSlot, ringMaterial, 0.88 * fade);
        this.setMaterialOpacity(activeSlot, accentMaterial, 0.82 * fade);
        this.setMaterialOpacity(activeSlot, coreMaterial, 0.92 * fade);
      };
    });
  }

  public specialCreated(coord: Coord, special: SpecialKind, options: VfxColorOptions = {}): void {
    this.specialCreation(coord, special, options);
  }

  /** Activate a single special effect. Combination effects use specialCombo. */
  public activateSpecial(
    special: SpecialKind,
    origin: VfxAnchor,
    options: BeamVfxOptions & NovaVfxOptions & BurstVfxOptions & PrismVfxOptions = {},
  ): void {
    switch (special) {
      case 'beamH':
      case 'beamV':
        this.beam(options.cells ?? [], {
          ...options,
          origin,
          direction: special === 'beamH' ? 'horizontal' : 'vertical',
        });
        break;
      case 'nova':
        this.nova(origin, options);
        break;
      case 'prism':
        this.prism(origin, options);
        break;
      case 'burst':
        this.burst(origin, options);
        break;
      default:
        this.burst(origin, options);
        break;
    }
  }

  /** A directional filament traveling through a line of cells. */
  public beam(cells: readonly Coord[], options: BeamVfxOptions = {}): void {
    const visualCells = this.uniqueCells(cells);
    const direction = options.direction ?? this.inferDirection(visualCells);
    const fallbackCenter = options.origin
      ? this.anchorPoint(options.origin, new THREE.Vector3()).clone()
      : undefined;
    const endpoints = this.lineEndpoints(visualCells, direction, fallbackCenter);
    const color = this.resolveColor(options.color, options.pieceType, direction === 'vertical' ? '#63ddff' : '#ffd36a');
    const intensity = clamp(options.intensity ?? 1, 0.6, 2.4);

    this.beginEffect('beam', options.duration ?? 0.72, (slot) => {
      const from = endpoints.from.clone().addScaledVector(this.boardNormal, 0.16);
      const to = endpoints.to.clone().addScaledVector(this.boardNormal, 0.16);
      const glowMaterial = this.takeMeshMaterial(slot, color, 0.8, true);
      const coreMaterial = this.takeMeshMaterial(slot, '#fffef1', 0.98, true);
      const tipMaterial = this.takeMeshMaterial(slot, this.specialAccent(direction === 'vertical' ? 'beamV' : 'beamH'), 0.9, true);
      const glow = this.addCylinderBetween(slot, from, to, 0.12 * intensity, glowMaterial);
      const core = this.addCylinderBetween(slot, from, to, 0.038 * intensity, coreMaterial);
      const pulse = this.addMesh(slot, 'beam-pulse', tipMaterial);
      pulse.position.copy(from);
      pulse.scale.setScalar(0.22 * intensity);
      const endCapA = this.addPlaneMesh(slot, 'beam-cap', from, tipMaterial);
      const endCapB = this.addPlaneMesh(slot, 'beam-cap', to, tipMaterial);
      this.emitParticles(slot, [from, to], 9, color, 0.16, 0.82, 0.68, 0.05);

      return (activeSlot, phase) => {
        const travel = phase.name === 'anticipation'
          ? phase.eased * 0.12
          : phase.name === 'impact'
            ? 0.12 + phase.eased * 0.88
            : 1;
        pulse.position.lerpVectors(from, to, travel);
        const width = phase.name === 'impact' ? lerp(1, 1.5, phase.eased) : 1;
        glow.scale.x = 0.12 * intensity * width;
        glow.scale.z = 0.12 * intensity * width;
        core.scale.x = 0.038 * intensity * width;
        core.scale.z = 0.038 * intensity * width;
        endCapA.scale.setScalar(width * (phase.name === 'dissipation' ? 1 - phase.eased : 1));
        endCapB.scale.copy(endCapA.scale);
        const fade = phase.name === 'dissipation' ? 1 - phase.eased : 1;
        this.setMaterialOpacity(activeSlot, glowMaterial, 0.8 * fade);
        this.setMaterialOpacity(activeSlot, coreMaterial, 0.98 * fade);
        this.setMaterialOpacity(activeSlot, tipMaterial, 0.9 * fade);
      };
    });
  }

  public lineBeam(cells: readonly Coord[], options: BeamVfxOptions = {}): void {
    this.beam(cells, options);
  }

  /** Radial nova: a broad, readable wave with a single central impact. */
  public nova(origin: VfxAnchor, options: NovaVfxOptions = {}): void {
    const center = this.anchorPoint(origin, this.temporary.point).clone().addScaledVector(this.boardNormal, 0.18);
    const color = this.resolveColor(options.color, options.pieceType, SPECIAL_COLORS.nova);
    const radius = Math.max(this.cellSize * 0.8, options.radius ?? this.cellSize * 1.8);
    const particleOrigins = options.cells?.length
      ? options.cells.map((cell) => this.worldPoint(cell, new THREE.Vector3()).addScaledVector(this.boardNormal, 0.14))
      : [center];

    this.beginEffect('nova', options.duration ?? 0.96, (slot) => {
      const ringMaterial = this.takeMeshMaterial(slot, color, 0.82, true);
      const haloMaterial = this.takeMeshMaterial(slot, '#ffd7ea', 0.42, true);
      const coreMaterial = this.takeMeshMaterial(slot, '#fff8f2', 0.95, true);
      const rings = [
        this.addPlaneMesh(slot, 'nova-ring', center, ringMaterial),
        this.addPlaneMesh(slot, 'nova-ring-wide', center, ringMaterial),
        this.addPlaneMesh(slot, 'nova-halo', center, haloMaterial),
      ];
      const core = this.addMesh(slot, 'nova-core', coreMaterial);
      core.position.copy(center);
      core.scale.setScalar(0.2);
      const spokes: THREE.Mesh[] = [];
      for (let index = 0; index < 4; index += 1) {
        const spoke = this.addMesh(slot, 'nova-spoke', haloMaterial);
        spoke.rotation.z = index * (Math.PI / 4);
        spoke.scale.set(0.2, 0.6, 1);
        spoke.position.copy(center);
        spokes.push(spoke);
      }
      this.emitParticles(slot, particleOrigins, 17, color, 0.34, 1.05, 0.88, -0.5);

      return (activeSlot, phase, delta) => {
        const radiusFactor = phase.name === 'anticipation'
          ? lerp(0.25, 0.52, phase.eased)
          : phase.name === 'impact'
            ? lerp(0.52, 1, phase.eased)
            : lerp(1, 1.55, phase.eased);
        rings[0].scale.setScalar((radius / 0.62) * radiusFactor);
        rings[1].scale.setScalar((radius / 0.62) * radiusFactor * 0.72);
        rings[2].scale.setScalar((radius / 0.62) * radiusFactor * 0.5);
        core.scale.setScalar((phase.name === 'dissipation' ? 1 - phase.eased : lerp(0.5, 1.4, phase.eased)) * 0.2);
        if (!this.reducedMotion) {
          rings[0].rotation.z += delta * 3.3;
          rings[1].rotation.z -= delta * 2.4;
          core.rotation.x += delta * 2.4;
          core.rotation.z += delta * 3.6;
        }
        for (let index = 0; index < spokes.length; index += 1) {
          const spoke = spokes[index];
          spoke.position.copy(center);
          spoke.position.addScaledVector(this.boardRight, Math.cos(index * Math.PI / 2) * radiusFactor * radius * 0.28);
          spoke.position.addScaledVector(this.boardDown, Math.sin(index * Math.PI / 2) * radiusFactor * radius * 0.28);
          spoke.scale.set(0.2 * radiusFactor, 0.6 * radiusFactor, 1);
        }
        const fade = phase.name === 'dissipation' ? 1 - phase.eased : 1;
        this.setMaterialOpacity(activeSlot, ringMaterial, 0.82 * fade);
        this.setMaterialOpacity(activeSlot, haloMaterial, 0.42 * fade);
        this.setMaterialOpacity(activeSlot, coreMaterial, 0.95 * fade);
      };
    });
  }

  /** Compact impact with a sharper center and a smaller debris envelope. */
  public burst(origin: VfxAnchor, options: BurstVfxOptions = {}): void {
    const center = this.anchorPoint(origin, this.temporary.point).clone().addScaledVector(this.boardNormal, 0.2);
    const color = this.resolveColor(options.color, options.pieceType, SPECIAL_COLORS.burst);
    const radius = Math.max(this.cellSize * 0.55, options.radius ?? this.cellSize * 1.1);
    this.beginEffect('burst', options.duration ?? 0.68, (slot) => {
      const ringMaterial = this.takeMeshMaterial(slot, color, 0.92, true);
      const coreMaterial = this.takeMeshMaterial(slot, '#fff3d0', 0.98, true);
      const shardMaterial = this.takeMeshMaterial(slot, '#ffdb91', 0.82, true);
      const ring = this.addPlaneMesh(slot, 'burst-ring', center, ringMaterial);
      const inner = this.addPlaneMesh(slot, 'burst-ring-inner', center, ringMaterial);
      const core = this.addMesh(slot, 'burst-core', coreMaterial);
      core.position.copy(center);
      core.scale.setScalar(0.18);
      const shards: THREE.Mesh[] = [];
      const shardCount = this.reducedMotion ? 4 : 6;
      for (let index = 0; index < shardCount; index += 1) {
        const shard = this.addMesh(slot, 'burst-shard', shardMaterial);
        shard.position.copy(center);
        shard.scale.setScalar(0.16);
        shards.push(shard);
      }
      if (options.particles !== false) {
        this.emitParticles(slot, [center], 12, color, 0.22, 0.9, 0.62, -0.8);
      }

      return (activeSlot, phase, delta) => {
        const expansion = phase.name === 'anticipation'
          ? lerp(0.25, 0.62, phase.eased)
          : phase.name === 'impact'
            ? lerp(0.62, 1, phase.eased)
            : lerp(1, 1.5, phase.eased);
        ring.scale.setScalar((radius / 0.62) * expansion);
        inner.scale.setScalar((radius / 0.62) * expansion * 0.62);
        core.scale.setScalar((phase.name === 'dissipation' ? 1 - phase.eased : lerp(0.5, 1.3, phase.eased)) * 0.18);
        if (!this.reducedMotion) {
          ring.rotation.z += delta * 5.4;
          inner.rotation.z -= delta * 7.2;
          core.rotation.z += delta * 6;
        }
        for (let index = 0; index < shards.length; index += 1) {
          const angle = index * (TAU / shards.length) + Math.PI / 6;
          const travel = expansion * radius * 0.36;
          shards[index].position.copy(center);
          shards[index].position.addScaledVector(this.boardRight, Math.cos(angle) * travel);
          shards[index].position.addScaledVector(this.boardDown, Math.sin(angle) * travel);
          shards[index].position.addScaledVector(this.boardNormal, Math.sin(expansion * 2 + index) * 0.06);
          shards[index].rotation.z = angle;
          shards[index].scale.setScalar((phase.name === 'dissipation' ? 1 - phase.eased : 1) * 0.16);
        }
        const fade = phase.name === 'dissipation' ? 1 - phase.eased : 1;
        this.setMaterialOpacity(activeSlot, ringMaterial, 0.92 * fade);
        this.setMaterialOpacity(activeSlot, coreMaterial, 0.98 * fade);
        this.setMaterialOpacity(activeSlot, shardMaterial, 0.82 * fade);
      };
    });
  }

  /** Board-directed prism rays; targets can be supplied by the logical special resolution. */
  public prism(origin: VfxAnchor, options: PrismVfxOptions = {}): void {
    const center = this.anchorPoint(origin, this.temporary.point).clone().addScaledVector(this.boardNormal, 0.2);
    const targets = options.targets?.length
      ? options.targets.map((cell) => this.worldPoint(cell, new THREE.Vector3()).addScaledVector(this.boardNormal, 0.18))
      : this.defaultPrismTargets(center);
    const color = this.resolveColor(options.color, options.pieceType, SPECIAL_COLORS.prism);

    this.beginEffect('prism', options.duration ?? 1.02, (slot) => {
      const coreMaterial = this.takeMeshMaterial(slot, '#fff7ff', 0.98, true);
      const rayMaterials = targets.map((_target, index) => this.takeMeshMaterial(slot, this.prismColor(index, color), 0.7, true));
      const core = this.addMesh(slot, 'prism-core', coreMaterial);
      core.position.copy(center);
      core.scale.setScalar(0.22);
      const rays: BeamSegment[] = [];
      for (let index = 0; index < targets.length; index += 1) {
        rays.push(this.addBeamSegment(slot, center, targets[index], 0.032, rayMaterials[index]));
      }
      const facets: THREE.Mesh[] = [];
      for (let index = 0; index < 4; index += 1) {
        const facet = this.addMesh(slot, 'prism-facet', rayMaterials[index % rayMaterials.length]);
        facet.position.copy(center);
        facet.scale.setScalar(0.16);
        facets.push(facet);
      }
      this.emitParticles(slot, [center, ...targets], 18, color, 0.12, 0.78, 0.9, 0.1);

      return (activeSlot, phase, delta) => {
        const rayProgress = phase.name === 'anticipation'
          ? phase.eased * 0.22
          : phase.name === 'impact'
            ? 0.22 + phase.eased * 0.78
            : 1;
        for (let index = 0; index < rays.length; index += 1) {
          this.setBeamSegmentProgress(rays[index], rayProgress);
          this.setMaterialOpacity(activeSlot, rayMaterials[index], 0.7 * (phase.name === 'dissipation' ? 1 - phase.eased : 1));
        }
        core.scale.setScalar((phase.name === 'dissipation' ? 1 - phase.eased : lerp(0.75, 1.3, phase.eased)) * 0.22);
        if (!this.reducedMotion) {
          core.rotation.x += delta * 3;
          core.rotation.z += delta * 2.4;
        }
        for (let index = 0; index < facets.length; index += 1) {
          const angle = index * (TAU / facets.length) + (this.reducedMotion ? 0 : this.time * 0.9);
          facets[index].position.copy(center);
          facets[index].position.addScaledVector(this.boardRight, Math.cos(angle) * 0.34);
          facets[index].position.addScaledVector(this.boardDown, Math.sin(angle) * 0.34);
          facets[index].scale.setScalar((phase.name === 'dissipation' ? 1 - phase.eased : 1) * 0.16);
        }
        this.setMaterialOpacity(activeSlot, coreMaterial, 0.98 * (phase.name === 'dissipation' ? 1 - phase.eased : 1));
      };
    });
  }

  /**
   * Combination effects intentionally share one timeline so their impact reads
   * as one decision, rather than a stack of unrelated flashes.
   */
  public specialCombo(
    origin: VfxAnchor,
    first: SpecialKind,
    second: SpecialKind,
    options: ComboVfxOptions = {},
  ): void {
    const center = this.anchorPoint(origin, this.temporary.point).clone().addScaledVector(this.boardNormal, 0.22);
    const cells = this.uniqueCells(options.cells ?? []);
    const beamPair = this.isBeamSpecial(first) && this.isBeamSpecial(second);
    const hasPrism = first === 'prism' || second === 'prism';
    const superNova = (first === 'nova' && second === 'burst') || (first === 'burst' && second === 'nova');
    const color = this.resolveColor(options.color, undefined, hasPrism ? SPECIAL_COLORS.prism : '#ffd36a');
    const intensity = clamp(options.intensity ?? (beamPair || hasPrism || superNova ? 1.3 : 1.15), 0.8, 2.8);

    this.beginEffect('combo', options.duration ?? (superNova ? 1.2 : 1.05), (slot) => {
      const ringMaterial = this.takeMeshMaterial(slot, color, 0.86, true);
      const coreMaterial = this.takeMeshMaterial(slot, '#fffdf0', 0.98, true);
      const accentMaterial = this.takeMeshMaterial(slot, this.comboAccent(first, second), 0.82, true);
      const rings = [
        this.addPlaneMesh(slot, 'combo-ring', center, ringMaterial),
        this.addPlaneMesh(slot, 'combo-ring-wide', center, accentMaterial),
      ];
      const core = this.addMesh(slot, hasPrism ? 'combo-prism-core' : 'combo-core', coreMaterial);
      core.position.copy(center);
      core.scale.setScalar(0.24);
      const segments: BeamSegment[] = [];
      const markers: THREE.Mesh[] = [];

      if (beamPair) {
        const horizontal = this.lineEndpoints(cells, 'horizontal', center);
        const vertical = this.lineEndpoints(cells, 'vertical', center);
        segments.push(this.addBeamSegment(slot, horizontal.from, horizontal.to, 0.09 * intensity, accentMaterial));
        segments.push(this.addBeamSegment(slot, vertical.from, vertical.to, 0.09 * intensity, accentMaterial));
        for (let index = 0; index < 4; index += 1) {
          const marker = this.addMesh(slot, 'combo-marker', accentMaterial);
          marker.position.copy(center);
          marker.scale.setScalar(0.16);
          markers.push(marker);
        }
      } else if (hasPrism) {
        const targets = cells.length > 0
          ? cells.slice(0, this.cellLimit(12)).map((cell) => this.worldPoint(cell, new THREE.Vector3()).addScaledVector(this.boardNormal, 0.18))
          : this.defaultPrismTargets(center);
        for (let index = 0; index < targets.length; index += 1) {
          const rayMaterial = this.takeMeshMaterial(slot, this.prismColor(index, color), 0.76, true);
          segments.push(this.addBeamSegment(slot, center, targets[index], 0.04 * intensity, rayMaterial));
        }
      } else {
        const directions = this.comboDirections(first, second);
        for (const direction of directions) {
          const end = center.clone().addScaledVector(direction, this.cellSize * (superNova ? 2.8 : 2.1));
          segments.push(this.addBeamSegment(slot, center, end, 0.055 * intensity, accentMaterial));
        }
      }

      const particleCount = superNova ? 30 : beamPair || hasPrism ? 24 : 20;
      this.emitParticles(slot, [center], particleCount, color, superNova ? 0.5 : 0.34, superNova ? 1.4 : 1.05, 1.05, -0.35);

      return (activeSlot, phase, delta) => {
        const expansion = phase.name === 'anticipation'
          ? lerp(0.32, 0.65, phase.eased)
          : phase.name === 'impact'
            ? lerp(0.65, 1.1, phase.eased)
            : lerp(1.1, superNova ? 2.0 : 1.65, phase.eased);
        rings[0].scale.setScalar(expansion * intensity);
        rings[1].scale.setScalar(expansion * 0.7 * intensity);
        core.scale.setScalar((phase.name === 'dissipation' ? 1 - phase.eased : lerp(0.7, superNova ? 1.65 : 1.3, phase.eased)) * 0.24 * intensity);
        if (!this.reducedMotion) {
          rings[0].rotation.z += delta * 4.2;
          rings[1].rotation.z -= delta * 6;
          core.rotation.x += delta * 3;
          core.rotation.z -= delta * 2.7;
        }
        const beamProgress = phase.name === 'anticipation'
          ? phase.eased * 0.18
          : phase.name === 'impact'
            ? 0.18 + phase.eased * 0.82
            : 1;
        for (const segment of segments) {
          this.setBeamSegmentProgress(segment, beamProgress);
        }
        for (let index = 0; index < markers.length; index += 1) {
          const angle = index * (TAU / markers.length) + (this.reducedMotion ? 0 : this.time * 1.2);
          markers[index].position.copy(center);
          markers[index].position.addScaledVector(this.boardRight, Math.cos(angle) * expansion * 0.72);
          markers[index].position.addScaledVector(this.boardDown, Math.sin(angle) * expansion * 0.72);
          markers[index].position.addScaledVector(this.boardNormal, 0.08);
          markers[index].scale.setScalar((phase.name === 'dissipation' ? 1 - phase.eased : 1) * 0.16 * intensity);
        }
        const fade = phase.name === 'dissipation' ? 1 - phase.eased : 1;
        this.setMaterialOpacity(activeSlot, ringMaterial, 0.86 * fade);
        this.setMaterialOpacity(activeSlot, accentMaterial, 0.82 * fade);
        this.setMaterialOpacity(activeSlot, coreMaterial, 0.98 * fade);
      };
    });
  }

  public combo(
    origin: VfxAnchor,
    first: SpecialKind,
    second: SpecialKind,
    options: ComboVfxOptions = {},
  ): void {
    this.specialCombo(origin, first, second, options);
  }

  /** Damage feedback for layered blockers; amount controls chip scale and density. */
  public blockerDamage(cells: readonly Coord[], options: BlockerDamageVfxOptions = {}): void {
    const visualCells = this.limitCells(this.uniqueCells(cells), this.cellLimit(28));
    if (visualCells.length === 0) return;
    const kind = options.kind ?? 'blocker';
    const color = this.resolveColor(options.color, options.pieceType, this.blockerColor(kind));
    const amount = clamp(options.amount ?? 1, 1, 4);
    const origins = visualCells.map((cell) => this.worldPoint(cell, new THREE.Vector3()).addScaledVector(this.boardNormal, 0.13));

    this.beginEffect('blocker-damage', options.duration ?? 0.62, (slot) => {
      const crackMaterial = this.takeMeshMaterial(slot, color, 0.88, true, true);
      const ringMaterial = this.takeMeshMaterial(slot, '#fff0c5', 0.55, true);
      const shells: THREE.Mesh[] = [];
      const rings: THREE.Mesh[] = [];
      for (const origin of origins) {
        const shell = this.addMesh(slot, 'blocker-shell', crackMaterial);
        shell.position.copy(origin);
        shell.scale.setScalar(0.7);
        shells.push(shell);
        rings.push(this.addPlaneMesh(slot, 'blocker-ring', origin, ringMaterial));
      }
      this.emitParticles(slot, origins, 5 + Math.floor(amount * 3), color, 0.18, 0.55, 0.58, -0.55);

      return (activeSlot, phase, delta) => {
        const crackScale = phase.name === 'anticipation'
          ? lerp(0.62, 0.84, phase.eased)
          : phase.name === 'impact'
            ? lerp(0.84, 1.12 + amount * 0.06, phase.eased)
            : lerp(1.12 + amount * 0.06, 1.42, phase.eased);
        const ringScale = phase.name === 'anticipation'
          ? lerp(0.55, 0.78, phase.eased)
          : phase.name === 'impact'
            ? lerp(0.78, 1.2, phase.eased)
            : lerp(1.2, 1.65, phase.eased);
        for (let index = 0; index < shells.length; index += 1) {
          shells[index].scale.setScalar(crackScale);
          rings[index].scale.setScalar(ringScale);
          if (!this.reducedMotion) shells[index].rotation.x += delta * 1.2 * (index + 1);
          rings[index].rotation.z = this.reducedMotion ? 0 : (index % 2 ? -1 : 1) * this.time * 1.5;
        }
        const fade = phase.name === 'dissipation' ? 1 - phase.eased : 1;
        this.setMaterialOpacity(activeSlot, crackMaterial, 0.88 * fade);
        this.setMaterialOpacity(activeSlot, ringMaterial, 0.55 * fade);
      };
    });
  }

  public damageBlocker(cells: readonly Coord[], options: BlockerDamageVfxOptions = {}): void {
    this.blockerDamage(cells, options);
  }

  /** A celebratory but compact completion burst anchored to objective cells. */
  public objectiveComplete(cells: readonly Coord[] = [], options: ObjectiveVfxOptions = {}): void {
    const center = options.center
      ? this.anchorPoint(options.center, this.temporary.point).clone()
      : this.averageWorld(this.uniqueCells(cells), this.temporary.point).clone();
    center.addScaledVector(this.boardNormal, 0.2);
    const color = this.resolveColor(options.color, options.pieceType, '#ffe29a');
    const origins = this.uniqueCells(cells).slice(0, this.cellLimit(16)).map((cell) => this.worldPoint(cell, new THREE.Vector3()).addScaledVector(this.boardNormal, 0.16));
    if (origins.length === 0) origins.push(center.clone());

    this.beginEffect('objective', options.duration ?? 0.9, (slot) => {
      slot.root.userData.objectiveLabel = options.label ?? '';
      const ringMaterial = this.takeMeshMaterial(slot, color, 0.9, true);
      const coreMaterial = this.takeMeshMaterial(slot, '#fffdf0', 0.96, true);
      const ring = this.addPlaneMesh(slot, 'objective-ring', center, ringMaterial);
      const ringWide = this.addPlaneMesh(slot, 'objective-ring-wide', center, ringMaterial);
      const core = this.addMesh(slot, 'objective-core', coreMaterial);
      core.position.copy(center);
      core.scale.setScalar(0.18);
      const stars: THREE.Mesh[] = [];
      for (let index = 0; index < 6; index += 1) {
        const star = this.addMesh(slot, 'objective-star', ringMaterial);
        star.position.copy(center);
        star.scale.setScalar(0.13);
        stars.push(star);
      }
      this.emitParticles(slot, origins, 15, color, 0.32, 0.72, 0.84, -0.2);

      return (activeSlot, phase, delta) => {
        const expansion = phase.name === 'anticipation'
          ? lerp(0.36, 0.75, phase.eased)
          : phase.name === 'impact'
            ? lerp(0.75, 1.15, phase.eased)
            : lerp(1.15, 1.72, phase.eased);
        ring.scale.setScalar(expansion);
        ringWide.scale.setScalar(expansion * 0.7);
        core.scale.setScalar((phase.name === 'dissipation' ? 1 - phase.eased : lerp(0.5, 1.3, phase.eased)) * 0.18);
        if (!this.reducedMotion) {
          ring.rotation.z += delta * 3.6;
          ringWide.rotation.z -= delta * 4.8;
          core.rotation.x += delta * 2.4;
        }
        for (let index = 0; index < stars.length; index += 1) {
          const angle = index * (TAU / stars.length) + (this.reducedMotion ? 0 : this.time * 0.8);
          const orbit = expansion * 0.54;
          stars[index].position.copy(center);
          stars[index].position.addScaledVector(this.boardRight, Math.cos(angle) * orbit);
          stars[index].position.addScaledVector(this.boardDown, Math.sin(angle) * orbit);
          stars[index].position.addScaledVector(this.boardNormal, 0.06);
          stars[index].rotation.z = angle;
          stars[index].scale.setScalar((phase.name === 'dissipation' ? 1 - phase.eased : 1) * 0.13);
        }
        const fade = phase.name === 'dissipation' ? 1 - phase.eased : 1;
        this.setMaterialOpacity(activeSlot, ringMaterial, 0.9 * fade);
        this.setMaterialOpacity(activeSlot, coreMaterial, 0.96 * fade);
      };
    });
  }

  public objective(cells: readonly Coord[] = [], options: ObjectiveVfxOptions = {}): void {
    this.objectiveComplete(cells, options);
  }

  /** A calm board-wide spiral cue used before the logical board is rebuilt. */
  public reshuffle(cells: readonly Coord[] = [], options: ReshuffleVfxOptions = {}): void {
    const normalized = this.uniqueCells(cells);
    const center = options.center
      ? this.anchorPoint(options.center, this.temporary.point).clone()
      : this.averageWorld(normalized, this.temporary.point).clone();
    center.addScaledVector(this.boardNormal, 0.15);
    const color = this.resolveColor(options.color, options.pieceType, '#a8e8ff');

    this.beginEffect('reshuffle', options.duration ?? 1.08, (slot) => {
      const ringMaterial = this.takeMeshMaterial(slot, color, 0.62, true);
      const coreMaterial = this.takeMeshMaterial(slot, '#e9ffff', 0.7, true);
      const ring = this.addPlaneMesh(slot, 'reshuffle-ring', center, ringMaterial);
      const ringInner = this.addPlaneMesh(slot, 'reshuffle-ring-inner', center, ringMaterial);
      const core = this.addMesh(slot, 'reshuffle-core', coreMaterial);
      core.position.copy(center);
      core.scale.setScalar(0.16);
      const orbiters: THREE.Mesh[] = [];
      const count = this.reducedMotion ? 5 : 8;
      for (let index = 0; index < count; index += 1) {
        const orbiter = this.addMesh(slot, 'reshuffle-orbiter', coreMaterial);
        orbiter.position.copy(center);
        orbiter.scale.setScalar(0.11);
        orbiters.push(orbiter);
      }
      const particleOrigins = normalized.length > 0
        ? normalized.slice(0, this.cellLimit(24)).map((cell) => this.worldPoint(cell, new THREE.Vector3()).addScaledVector(this.boardNormal, 0.12))
        : [center];
      this.emitParticles(slot, particleOrigins, 16, color, 0.22, 0.68, 1, 0);

      return (activeSlot, phase, delta) => {
        const sweep = phase.name === 'anticipation'
          ? lerp(0.25, 0.7, phase.eased)
          : phase.name === 'impact'
            ? lerp(0.7, 1.05, phase.eased)
            : lerp(1.05, 1.45, phase.eased);
        ring.scale.setScalar(sweep);
        ringInner.scale.setScalar(sweep * 0.64);
        core.scale.setScalar((phase.name === 'dissipation' ? 1 - phase.eased : lerp(0.55, 1.15, phase.eased)) * 0.16);
        if (!this.reducedMotion) {
          ring.rotation.z += delta * 3;
          ringInner.rotation.z -= delta * 4.5;
        }
        for (let index = 0; index < orbiters.length; index += 1) {
          const angle = index * (TAU / orbiters.length) + (this.reducedMotion ? 0 : this.time * 1.7);
          const orbit = sweep * 0.62;
          orbiters[index].position.copy(center);
          orbiters[index].position.addScaledVector(this.boardRight, Math.cos(angle) * orbit);
          orbiters[index].position.addScaledVector(this.boardDown, Math.sin(angle) * orbit);
          orbiters[index].position.addScaledVector(this.boardNormal, 0.04);
          orbiters[index].scale.setScalar((phase.name === 'dissipation' ? 1 - phase.eased : 1) * 0.11);
        }
        const fade = phase.name === 'dissipation' ? 1 - phase.eased : 1;
        this.setMaterialOpacity(activeSlot, ringMaterial, 0.62 * fade);
        this.setMaterialOpacity(activeSlot, coreMaterial, 0.7 * fade);
      };
    });
  }

  /** Celebration effect for a completed level. */
  public win(cells: readonly Coord[] = [], options: WinVfxOptions = {}): void {
    const normalized = this.uniqueCells(options.cells ?? cells);
    const center = options.center
      ? this.anchorPoint(options.center, this.temporary.point).clone()
      : this.averageWorld(normalized, this.temporary.point).clone();
    center.addScaledVector(this.boardNormal, 0.22);
    const color = this.resolveColor(options.color, options.pieceType, '#ffe7a6');

    this.beginEffect('win', options.duration ?? 1.65, (slot) => {
      const ringMaterial = this.takeMeshMaterial(slot, color, 0.88, true);
      const accentMaterial = this.takeMeshMaterial(slot, '#9beed2', 0.75, true);
      const coreMaterial = this.takeMeshMaterial(slot, '#fffef2', 0.96, true);
      const rings = [
        this.addPlaneMesh(slot, 'win-ring', center, ringMaterial),
        this.addPlaneMesh(slot, 'win-ring-wide', center, accentMaterial),
      ];
      const core = this.addMesh(slot, 'win-core', coreMaterial);
      core.position.copy(center);
      core.scale.setScalar(0.2);
      const stars: THREE.Mesh[] = [];
      const starCount = this.reducedMotion ? 6 : 12;
      for (let index = 0; index < starCount; index += 1) {
        const star = this.addMesh(slot, 'win-star', index % 2 === 0 ? ringMaterial : accentMaterial);
        star.position.copy(center);
        star.scale.setScalar(0.12 + (index % 3) * 0.025);
        stars.push(star);
      }
      this.emitParticles(slot, [center], this.reducedMotion ? 12 : 28, color, 0.6, 1.1, 1.35, -0.8);

      return (activeSlot, phase, delta) => {
        const expansion = phase.name === 'anticipation'
          ? lerp(0.25, 0.75, phase.eased)
          : phase.name === 'impact'
            ? lerp(0.75, 1.2, phase.eased)
            : lerp(1.2, 2.2, phase.eased);
        rings[0].scale.setScalar(expansion);
        rings[1].scale.setScalar(expansion * 0.72);
        core.scale.setScalar((phase.name === 'dissipation' ? 1 - phase.eased : lerp(0.7, 1.45, phase.eased)) * 0.2);
        if (!this.reducedMotion) {
          rings[0].rotation.z += delta * 3;
          rings[1].rotation.z -= delta * 3.9;
          core.rotation.x += delta * 2.4;
          core.rotation.z += delta * 1.8;
        }
        for (let index = 0; index < stars.length; index += 1) {
          const angle = index * (TAU / stars.length) + (this.reducedMotion ? 0 : this.time * (0.5 + index * 0.015));
          const orbit = expansion * (0.64 + (index % 3) * 0.12);
          stars[index].position.copy(center);
          stars[index].position.addScaledVector(this.boardRight, Math.cos(angle) * orbit);
          stars[index].position.addScaledVector(this.boardDown, Math.sin(angle) * orbit);
          stars[index].position.addScaledVector(this.boardNormal, 0.06 + (index % 2) * 0.05);
          stars[index].rotation.z = angle;
          stars[index].scale.setScalar((phase.name === 'dissipation' ? 1 - phase.eased : 1) * (0.12 + (index % 3) * 0.025));
        }
        const fade = phase.name === 'dissipation' ? 1 - phase.eased : 1;
        this.setMaterialOpacity(activeSlot, ringMaterial, 0.88 * fade);
        this.setMaterialOpacity(activeSlot, accentMaterial, 0.75 * fade);
        this.setMaterialOpacity(activeSlot, coreMaterial, 0.96 * fade);
      };
    });
  }

  /** Quiet failure feedback: a cool pulse and settling motes, with no harsh flash. */
  public loss(cells: readonly Coord[] = [], options: LossVfxOptions = {}): void {
    const normalized = this.uniqueCells(options.cells ?? cells);
    const center = options.center
      ? this.anchorPoint(options.center, this.temporary.point).clone()
      : this.averageWorld(normalized, this.temporary.point).clone();
    center.addScaledVector(this.boardNormal, 0.14);
    const color = this.resolveColor(options.color, options.pieceType, '#8ba4c9');

    this.beginEffect('loss', options.duration ?? 1.05, (slot) => {
      const ringMaterial = this.takeMeshMaterial(slot, color, 0.54, true);
      const coreMaterial = this.takeMeshMaterial(slot, '#c8d4e7', 0.4, true);
      const ring = this.addPlaneMesh(slot, 'loss-ring', center, ringMaterial);
      const inner = this.addPlaneMesh(slot, 'loss-ring-inner', center, ringMaterial);
      const core = this.addMesh(slot, 'loss-core', coreMaterial);
      core.position.copy(center);
      core.scale.setScalar(0.16);
      const motes: THREE.Mesh[] = [];
      const moteCount = this.reducedMotion ? 4 : 7;
      for (let index = 0; index < moteCount; index += 1) {
        const mote = this.addMesh(slot, 'loss-mote', coreMaterial);
        mote.position.copy(center);
        mote.scale.setScalar(0.09);
        motes.push(mote);
      }
      this.emitParticles(slot, [center], this.reducedMotion ? 5 : 10, color, 0.24, 0.28, 1, -0.12);

      return (activeSlot, phase, delta) => {
        const expansion = phase.name === 'anticipation'
          ? lerp(0.35, 0.7, phase.eased)
          : phase.name === 'impact'
            ? lerp(0.7, 0.95, phase.eased)
            : lerp(0.95, 1.3, phase.eased);
        ring.scale.setScalar(expansion);
        inner.scale.setScalar(expansion * 0.62);
        core.scale.setScalar((phase.name === 'dissipation' ? 1 - phase.eased : lerp(0.7, 0.95, phase.eased)) * 0.16);
        if (!this.reducedMotion) {
          ring.rotation.z -= delta * 1.5;
          inner.rotation.z += delta * 2.1;
        }
        for (let index = 0; index < motes.length; index += 1) {
          const angle = index * (TAU / motes.length);
          const travel = expansion * (0.35 + index * 0.025);
          motes[index].position.copy(center);
          motes[index].position.addScaledVector(this.boardRight, Math.cos(angle) * travel);
          motes[index].position.addScaledVector(this.boardDown, Math.sin(angle) * travel - phase.normalized * 0.2);
          motes[index].position.addScaledVector(this.boardNormal, 0.04);
          motes[index].scale.setScalar((phase.name === 'dissipation' ? 1 - phase.eased : 1) * 0.09);
        }
        const fade = phase.name === 'dissipation' ? 1 - phase.eased : 1;
        this.setMaterialOpacity(activeSlot, ringMaterial, 0.54 * fade);
        this.setMaterialOpacity(activeSlot, coreMaterial, 0.4 * fade);
      };
    });
  }

  /** Subtle pulse for a hazard tick; intentionally shares the blocker budget. */
  public hazard(cells: readonly Coord[], options: VfxColorOptions = {}): void {
    const visualCells = this.limitCells(this.uniqueCells(cells), this.cellLimit(24));
    if (visualCells.length === 0) return;
    const color = this.resolveColor(options.color, options.pieceType, '#ff7e7e');
    this.beginEffect('hazard', options.duration ?? 0.52, (slot) => {
      const material = this.takeMeshMaterial(slot, color, 0.72, true);
      const meshes = visualCells.map((cell) => {
        const mesh = this.addPlaneMesh(slot, 'hazard-ring', this.worldPoint(cell, new THREE.Vector3()).addScaledVector(this.boardNormal, 0.1), material);
        mesh.scale.setScalar(0.4);
        return mesh;
      });
      this.emitParticles(slot, visualCells.map((cell) => this.worldPoint(cell, new THREE.Vector3())), 4 + visualCells.length, color, 0.12, 0.4, 0.45, 0);
      return (activeSlot, phase) => {
        const expansion = phase.name === 'anticipation'
          ? lerp(0.4, 0.8, phase.eased)
          : phase.name === 'impact'
            ? lerp(0.8, 1.1, phase.eased)
            : lerp(1.1, 1.5, phase.eased);
        for (const mesh of meshes) mesh.scale.setScalar(expansion);
        this.setMaterialOpacity(activeSlot, material, 0.72 * (phase.name === 'dissipation' ? 1 - phase.eased : 1));
      };
    });
  }

  /** Dispatch a logic move event into a visual treatment. */
  public playMoveEvent(event: MoveEvent): void {
    if (!event || !this.enabled || this.disposed) return;
    const cells = event.cells ?? [];
    switch (event.kind) {
      case 'swap':
        if (event.from && event.to) this.swap(event.from, event.to, { color: event.color });
        break;
      case 'invalid':
        if (event.from && event.to) this.invalidSwap(event.from, event.to, { color: event.color });
        break;
      case 'clear':
        this.clear(cells, { color: event.color, cascade: event.cascade });
        break;
      case 'damage':
        this.blockerDamage(cells, { color: event.color, amount: event.amount });
        break;
      case 'special':
        this.specialCreation(cells[0] ?? event.from ?? { r: 0, c: 0 }, event.special ?? 'burst', { color: event.color });
        break;
      case 'spawn':
        this.spawn(cells, { color: event.color });
        break;
      case 'cascade':
        this.cascade(event.cascade ?? 1, cells, { color: event.color });
        break;
      case 'reshuffle':
        this.reshuffle(cells, { color: event.color });
        break;
      case 'objective':
        this.objectiveComplete(cells, { color: event.color, label: event.label });
        break;
      case 'hazard':
        this.hazard(cells, { color: event.color, intensity: event.amount });
        break;
      case 'combo':
        this.specialCombo(
          cells[0] ?? event.from ?? { r: 0, c: 0 },
          event.special ?? 'burst',
          'nova',
          { cells, color: event.color, label: event.label, intensity: event.amount },
        );
        break;
      default:
        break;
    }
  }

  public play(event: MoveEvent): void {
    this.playMoveEvent(event);
  }

  public playEvents(events: readonly MoveEvent[]): void {
    for (const event of events) this.playMoveEvent(event);
  }

  /** Release all pooled resources owned by the director. */
  public dispose(): void {
    if (this.disposed) return;
    this.clearAll();
    this.detach();
    for (const geometry of this.geometryCache.values()) geometry.dispose();
    this.geometryCache.clear();
    for (const materials of this.materialPool.values()) {
      for (const material of materials) material.dispose();
    }
    this.materialPool.clear();
    for (const field of this.particlePool) field.geometry.dispose();
    this.particlePool.length = 0;
    this.root.clear();
    this.disposed = true;
  }

  private beginEffect(kind: VfxEffectKind, duration: number, setup: (slot: EffectSlot) => EffectUpdater): EffectSlot | undefined {
    if (!this.enabled || this.disposed) return undefined;
    const slot = this.acquire(kind);
    if (!slot) return undefined;
    slot.duration = duration === Infinity ? Infinity : this.scaledDuration(duration);
    slot.age = 0;
    slot.startedAt = this.time;
    slot.update = setup(slot);
    return slot;
  }

  private acquire(kind: VfxEffectKind): EffectSlot | undefined {
    let slot = this.slots.find((candidate) => !candidate.active);
    if (!slot && this.slots.length < this.maxEffects) {
      slot = {
        root: new THREE.Group(),
        id: this.sequence++,
        kind,
        active: false,
        persistent: false,
        age: 0,
        duration: 0,
        startedAt: 0,
        particles: [],
        materials: [],
      };
      slot.root.name = `vfx-effect-${slot.id}`;
      slot.root.matrixAutoUpdate = true;
      this.effectLayer.add(slot.root);
      this.slots.push(slot);
    }
    if (!slot) {
      const recyclable = this.slots
        .filter((candidate) => candidate.active && !candidate.persistent)
        .sort((left, right) => left.startedAt - right.startedAt)[0];
      if (!recyclable) return undefined;
      this.release(recyclable);
      slot = recyclable;
    }

    slot.kind = kind;
    slot.active = true;
    slot.persistent = false;
    slot.age = 0;
    slot.duration = 0;
    slot.startedAt = this.time;
    slot.update = undefined;
    slot.root.visible = true;
    slot.root.position.set(0, 0, 0);
    slot.root.quaternion.identity();
    slot.root.scale.setScalar(1);
    slot.root.userData = { vfxKind: kind, vfxId: slot.id };
    return slot;
  }

  private release(slot: EffectSlot): void {
    if (!slot.active) return;
    for (const field of [...slot.particles]) this.releaseParticleField(slot, field);
    for (const material of [...slot.materials]) {
      this.releaseMaterial(material);
    }
    slot.materials.length = 0;
    while (slot.root.children.length > 0) slot.root.remove(slot.root.children[slot.root.children.length - 1]);
    slot.root.visible = false;
    slot.active = false;
    slot.persistent = false;
    slot.update = undefined;
    for (const [key, selection] of this.selectionSlots) {
      if (selection === slot) this.selectionSlots.delete(key);
    }
  }

  private releaseMaterial(material: THREE.Material): void {
    const poolKey = material.userData.vfxPoolKey as string | undefined;
    if (!poolKey) {
      material.dispose();
      return;
    }
    material.visible = true;
    material.opacity = 0;
    const bucket = this.materialPool.get(poolKey) ?? [];
    bucket.push(material);
    this.materialPool.set(poolKey, bucket);
  }

  private updateParticles(slot: EffectSlot, deltaSeconds: number): void {
    for (const field of [...slot.particles]) {
      field.age += deltaSeconds;
      const life = clamp01(field.age / field.duration);
      const fadeIn = smoothstep(Math.min(1, life / 0.14));
      const fadeOut = 1 - smoothstep(Math.max(0, (life - 0.52) / 0.48));
      if (field.material) field.material.opacity = field.baseOpacity * fadeIn * fadeOut;
      for (let index = 0; index < field.count; index += 1) {
        const offset = index * 3;
        field.positions[offset] += field.velocities[offset] * deltaSeconds;
        field.positions[offset + 1] += field.velocities[offset + 1] * deltaSeconds;
        field.positions[offset + 2] += field.velocities[offset + 2] * deltaSeconds;
        field.velocities[offset] += field.gravity.x * deltaSeconds;
        field.velocities[offset + 1] += field.gravity.y * deltaSeconds;
        field.velocities[offset + 2] += field.gravity.z * deltaSeconds;
        const drag = Math.max(0, 1 - field.drag * deltaSeconds);
        field.velocities[offset] *= drag;
        field.velocities[offset + 1] *= drag;
        field.velocities[offset + 2] *= drag;
      }
      field.positionAttribute.needsUpdate = true;
      if (field.age >= field.duration) this.releaseParticleField(slot, field);
    }
  }

  private releaseParticleField(slot: EffectSlot, field: ParticleField): void {
    const index = slot.particles.indexOf(field);
    if (index >= 0) slot.particles.splice(index, 1);
    slot.root.remove(field.points);
    this.activeParticleCount = Math.max(0, this.activeParticleCount - field.count);
    field.count = 0;
    field.age = 0;
    field.points.visible = false;
    field.geometry.setDrawRange(0, 0);
    if (field.material) {
      const materialIndex = slot.materials.indexOf(field.material);
      if (materialIndex >= 0) slot.materials.splice(materialIndex, 1);
      this.releaseMaterial(field.material);
      field.material = undefined;
    }
    this.particlePool.push(field);
  }

  private emitParticles(
    slot: EffectSlot,
    origins: readonly THREE.Vector3[],
    requestedCount: number,
    color: THREE.Color,
    spread: number,
    speed: number,
    duration: number,
    gravity: number,
  ): void {
    if (origins.length === 0 || this.particleBudget <= this.activeParticleCount || requestedCount <= 0) return;
    const qualityFactor = this.quality === 'low' ? 0.45 : this.quality === 'medium' ? 0.72 : 1;
    const motionFactor = this.reducedMotion ? 0.35 : 1;
    const available = this.particleBudget - this.activeParticleCount;
    const count = Math.min(
      this.particleCapacity,
      available,
      Math.max(1, Math.floor(requestedCount * qualityFactor * motionFactor)),
    );
    if (count <= 0) return;

    const field = this.particlePool.pop() ?? this.createParticleField();
    field.count = count;
    field.age = 0;
    field.duration = this.scaledDuration(duration);
    field.gravity.copy(this.boardNormal).multiplyScalar(gravity);
    field.drag = 0.7;
    field.baseOpacity = this.reducedMotion ? 0.52 : 0.76;
    field.geometry.setDrawRange(0, count);
    field.points.position.set(0, 0, 0);
    field.points.visible = true;
    field.points.frustumCulled = false;

    const positions = field.positions;
    const velocities = field.velocities;
    const colors = field.colorAttribute.array as Float32Array;
    for (let index = 0; index < count; index += 1) {
      const offset = index * 3;
      const source = origins[Math.floor(this.random() * origins.length)];
      const direction = this.randomDirection();
      const radial = spread * (0.35 + this.random() * 0.65);
      positions[offset] = source.x + direction.x * radial;
      positions[offset + 1] = source.y + direction.y * radial;
      positions[offset + 2] = source.z + direction.z * radial;
      const launch = speed * (0.55 + this.random() * 0.75);
      velocities[offset] = direction.x * launch;
      velocities[offset + 1] = direction.y * launch;
      velocities[offset + 2] = direction.z * launch;

      const variation = 0.82 + this.random() * 0.3;
      colors[offset] = clamp01(color.r * variation);
      colors[offset + 1] = clamp01(color.g * variation);
      colors[offset + 2] = clamp01(color.b * variation);
    }
    field.positionAttribute.needsUpdate = true;
    field.colorAttribute.needsUpdate = true;
    const material = this.takePointsMaterial(slot, 0.09 * (this.cellSize > 1 ? this.cellSize : 1), field.baseOpacity);
    field.material = material;
    field.points.material = material;
    slot.root.add(field.points);
    slot.particles.push(field);
    this.activeParticleCount += count;
  }

  private createParticleField(): ParticleField {
    const positions = new Float32Array(this.particleCapacity * 3);
    const velocities = new Float32Array(this.particleCapacity * 3);
    const colors = new Float32Array(this.particleCapacity * 3);
    const geometry = new THREE.BufferGeometry();
    const positionAttribute = new THREE.Float32BufferAttribute(positions, 3);
    const colorAttribute = new THREE.Float32BufferAttribute(colors, 3);
    geometry.setAttribute('position', positionAttribute);
    geometry.setAttribute('color', colorAttribute);
    geometry.setDrawRange(0, 0);
    const points = new THREE.Points(geometry);
    points.frustumCulled = false;
    return {
      points,
      geometry,
      positionAttribute,
      colorAttribute,
      positions,
      velocities,
      count: 0,
      age: 0,
      duration: 0.1,
      gravity: new THREE.Vector3(),
      drag: 0.7,
      baseOpacity: 0.76,
    };
  }

  private takePointsMaterial(slot: EffectSlot, size: number, opacity: number): THREE.PointsMaterial {
    const key = 'points';
    const bucket = this.materialPool.get(key) ?? [];
    const pooled = bucket.pop();
    if (bucket.length > 0) this.materialPool.set(key, bucket);
    else this.materialPool.delete(key);
    const material = pooled instanceof THREE.PointsMaterial
      ? pooled
      : new THREE.PointsMaterial();
    material.name = 'vfx-points';
    material.color.set('#ffffff');
    material.size = size;
    material.sizeAttenuation = true;
    material.vertexColors = true;
    material.transparent = true;
    material.opacity = opacity;
    material.depthWrite = false;
    material.depthTest = false;
    material.blending = THREE.AdditiveBlending;
    material.userData.vfxPoolKey = key;
    slot.materials.push(material);
    return material;
  }

  private takeMeshMaterial(
    slot: EffectSlot,
    color: ColorRepresentation,
    opacity: number,
    additive: boolean,
    wireframe = false,
  ): THREE.MeshBasicMaterial {
    const key = `mesh:${additive ? 'add' : 'normal'}:${wireframe ? 'wire' : 'solid'}`;
    const bucket = this.materialPool.get(key) ?? [];
    const pooled = bucket.pop();
    if (bucket.length > 0) this.materialPool.set(key, bucket);
    else this.materialPool.delete(key);
    const material = pooled instanceof THREE.MeshBasicMaterial
      ? pooled
      : new THREE.MeshBasicMaterial();
    material.name = 'vfx-mesh';
    material.color.set(color);
    material.transparent = true;
    material.opacity = opacity;
    material.depthWrite = false;
    material.depthTest = false;
    material.side = THREE.DoubleSide;
    material.blending = additive ? THREE.AdditiveBlending : THREE.NormalBlending;
    material.wireframe = wireframe;
    material.toneMapped = false;
    material.userData.vfxPoolKey = key;
    slot.materials.push(material);
    return material;
  }

  private addMesh(slot: EffectSlot, geometryKey: string, material: THREE.Material): THREE.Mesh {
    const mesh = new THREE.Mesh(this.geometry(geometryKey), material);
    mesh.renderOrder = 20;
    slot.root.add(mesh);
    return mesh;
  }

  private addPlaneMesh(slot: EffectSlot, geometryKey: string, position: THREE.Vector3, material: THREE.Material): THREE.Mesh {
    const mesh = this.addMesh(slot, geometryKey, material);
    mesh.position.copy(position);
    mesh.quaternion.copy(this.planeQuaternion);
    return mesh;
  }

  private addBeamSegment(
    slot: EffectSlot,
    from: THREE.Vector3,
    to: THREE.Vector3,
    radius: number,
    material: THREE.Material,
  ): BeamSegment {
    const segment: BeamSegment = {
      mesh: this.addCylinderBetween(slot, from, to, radius, material),
      from: from.clone(),
      to: to.clone(),
      radius,
    };
    return segment;
  }

  private addCylinderBetween(
    slot: EffectSlot,
    from: THREE.Vector3,
    to: THREE.Vector3,
    radius: number,
    material: THREE.Material,
  ): THREE.Mesh {
    const mesh = this.addMesh(slot, 'unit-cylinder', material);
    this.setCylinderBetween(mesh, from, to, radius);
    return mesh;
  }

  private setCylinderBetween(mesh: THREE.Mesh, from: THREE.Vector3, to: THREE.Vector3, radius: number): void {
    const direction = this.temporary.direction.copy(to).sub(from);
    const length = Math.max(EPSILON, direction.length());
    this.temporary.midpoint.copy(from).add(to).multiplyScalar(0.5);
    mesh.position.copy(this.temporary.midpoint);
    mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
    mesh.scale.set(radius, length, radius);
  }

  private setBeamSegmentProgress(segment: BeamSegment, progress: number): void {
    const t = clamp01(progress);
    const end = this.temporary.pointC.lerpVectors(segment.from, segment.to, t);
    this.setCylinderBetween(segment.mesh, segment.from, end, segment.radius);
  }

  private setMaterialOpacity(slot: EffectSlot, material: THREE.Material, opacity: number): void {
    if (slot.materials.indexOf(material) < 0) return;
    material.opacity = clamp01(opacity);
    material.transparent = true;
  }

  private geometry(key: string): THREE.BufferGeometry {
    const existing = this.geometryCache.get(key);
    if (existing) return existing;
    let created: THREE.BufferGeometry;
    switch (key) {
      case 'selection-halo':
        created = new THREE.CircleGeometry(0.82, 32);
        break;
      case 'selection-ring':
        created = new THREE.RingGeometry(0.62, 0.69, 32);
        break;
      case 'diamond':
      case 'special-orbiter':
      case 'objective-star':
      case 'win-star':
      case 'reshuffle-orbiter':
      case 'loss-mote':
        created = new THREE.OctahedronGeometry(0.14, 0);
        break;
      case 'swap-pulse':
      case 'beam-pulse':
      case 'spawn-core':
      case 'cascade-core':
      case 'special-core':
      case 'nova-core':
      case 'burst-core':
      case 'prism-core':
      case 'combo-core':
      case 'combo-prism-core':
      case 'objective-core':
      case 'reshuffle-core':
      case 'loss-core':
        created = new THREE.IcosahedronGeometry(0.16, 1);
        break;
      case 'invalid-ring':
      case 'anticipation-ring':
      case 'clear-ring':
      case 'cascade-ring':
      case 'special-ring':
      case 'nova-ring':
      case 'burst-ring':
      case 'objective-ring':
      case 'reshuffle-ring':
      case 'loss-ring':
      case 'hazard-ring':
      case 'blocker-ring':
        created = new THREE.RingGeometry(0.56, 0.64, 32);
        break;
      case 'anticipation-core':
      case 'clear-core':
        created = new THREE.CircleGeometry(0.42, 24);
        break;
      case 'cascade-ring-inner':
      case 'special-ring-inner':
      case 'nova-ring-wide':
      case 'nova-halo':
      case 'burst-ring-inner':
      case 'combo-ring':
      case 'combo-ring-wide':
      case 'objective-ring-wide':
      case 'reshuffle-ring-inner':
      case 'loss-ring-inner':
        created = new THREE.RingGeometry(0.34, 0.43, 28);
        break;
      case 'special-beam-glyph':
      case 'special-prism-ray':
      case 'nova-spoke':
        created = new THREE.PlaneGeometry(1, 0.11, 1, 1);
        break;
      case 'special-nova-glyph':
        created = new THREE.RingGeometry(0.22, 0.32, 8);
        break;
      case 'special-burst-glyph':
      case 'burst-shard':
      case 'combo-marker':
        created = new THREE.ConeGeometry(0.14, 0.42, 4, 1, false);
        break;
      case 'blocker-shell':
        created = new THREE.IcosahedronGeometry(0.52, 1);
        break;
      case 'unit-cylinder':
        created = new THREE.CylinderGeometry(1, 1, 1, 12, 1, false);
        break;
      case 'beam-cap':
        created = new THREE.RingGeometry(0.18, 0.24, 16);
        break;
      default:
        created = new THREE.CircleGeometry(0.3, 20);
        break;
    }
    this.geometryCache.set(key, created);
    return created;
  }

  private worldPoint(coord: Coord, target: THREE.Vector3): THREE.Vector3 {
    const safeCoord = { r: Number.isFinite(coord.r) ? coord.r : 0, c: Number.isFinite(coord.c) ? coord.c : 0 };
    if (this.coordinateToWorld) {
      const mapped = this.coordinateToWorld(safeCoord, target);
      if (mapped && mapped !== target) target.copy(mapped);
    } else {
      target.copy(this.origin);
      target.addScaledVector(this.boardRight, safeCoord.c * this.cellSize);
      target.addScaledVector(this.boardDown, safeCoord.r * this.cellSize);
    }
    if (!isFiniteVector(target)) target.copy(this.origin);
    return target;
  }

  private normalizedOr(value: THREE.Vector3 | undefined, fallback: THREE.Vector3): THREE.Vector3 {
    const result = (value ?? fallback).clone();
    return result.lengthSq() > EPSILON ? result.normalize() : fallback.clone().normalize();
  }

  private anchorPoint(anchor: VfxAnchor | undefined, target: THREE.Vector3): THREE.Vector3 {
    if (!anchor) return target.copy(this.origin);
    if (anchor instanceof THREE.Vector3) return target.copy(anchor);
    return this.worldPoint(anchor, target);
  }

  private averageWorld(cells: readonly Coord[], target: THREE.Vector3): THREE.Vector3 {
    if (cells.length === 0) return target.copy(this.origin);
    target.set(0, 0, 0);
    for (const cell of cells) target.add(this.worldPoint(cell, this.temporary.sum));
    target.multiplyScalar(1 / cells.length);
    return target;
  }

  private uniqueCells(cells: readonly Coord[]): Coord[] {
    const unique: Coord[] = [];
    const seen = new Set<string>();
    for (const cell of cells) {
      if (!cell || !Number.isFinite(cell.r) || !Number.isFinite(cell.c)) continue;
      const normalized = { r: Math.round(cell.r), c: Math.round(cell.c) };
      const key = this.coordKey(normalized);
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(normalized);
    }
    return unique;
  }

  private limitCells(cells: readonly Coord[], limit: number): Coord[] {
    if (cells.length <= limit) return [...cells];
    const result: Coord[] = [];
    const seen = new Set<string>();
    for (let index = 0; index < limit; index += 1) {
      const sourceIndex = Math.round((index * (cells.length - 1)) / Math.max(1, limit - 1));
      const cell = cells[sourceIndex];
      const key = this.coordKey(cell);
      if (!seen.has(key)) {
        seen.add(key);
        result.push(cell);
      }
    }
    return result;
  }

  private cellLimit(highQualityLimit: number): number {
    if (this.quality === 'low') return Math.max(6, Math.floor(highQualityLimit * 0.45));
    if (this.quality === 'medium') return Math.max(8, Math.floor(highQualityLimit * 0.72));
    return highQualityLimit;
  }

  private coordKey(coord: Coord): string {
    return `${Math.round(coord.r)}:${Math.round(coord.c)}`;
  }

  private resolveColor(color: ColorRepresentation | undefined, pieceType: PieceType | undefined, fallback: ColorRepresentation): THREE.Color {
    if (color !== undefined) return new THREE.Color(color);
    if (pieceType !== undefined) {
      const pieceColor = this.pieceColors[pieceType] ?? DEFAULT_PIECE_COLORS[pieceType] ?? fallback;
      return new THREE.Color(pieceColor);
    }
    return new THREE.Color(fallback);
  }

  private specialAccent(special: SpecialKind): ColorRepresentation {
    switch (special) {
      case 'beamH': return '#fff1a1';
      case 'beamV': return '#b9f4ff';
      case 'nova': return '#ffc4d9';
      case 'prism': return '#f4c6ff';
      case 'burst': return '#ffd49f';
      default: return '#fff3c1';
    }
  }

  private comboAccent(first: SpecialKind, second: SpecialKind): ColorRepresentation {
    if (this.isBeamSpecial(first) && this.isBeamSpecial(second)) return '#fff1a1';
    if (first === 'prism' || second === 'prism') return '#f3c5ff';
    if (first === 'nova' || second === 'nova') return '#ffb4cb';
    return '#ffd09b';
  }

  private blockerColor(kind: string): ColorRepresentation {
    switch (kind) {
      case 'glass': return '#8ce7ff';
      case 'bramble': return '#ba87e8';
      case 'wax': return '#ffc66f';
      case 'lock': return '#ff737c';
      case 'meteor': return '#ff795b';
      default: return '#d4c7af';
    }
  }

  private isBeamSpecial(special: SpecialKind): boolean {
    return special === 'beamH' || special === 'beamV';
  }

  private inferDirection(cells: readonly Coord[]): VfxBeamDirection {
    if (cells.length < 2) return 'horizontal';
    const first = cells[0];
    const last = cells[cells.length - 1];
    if (first.r === last.r) return 'horizontal';
    if (first.c === last.c) return 'vertical';
    return 'diagonal';
  }

  private lineEndpoints(
    cells: readonly Coord[],
    direction: VfxBeamDirection,
    fallbackCenter?: THREE.Vector3,
  ): { from: THREE.Vector3; to: THREE.Vector3 } {
    if (cells.length > 0) {
      const normalized = this.uniqueCells(cells);
      if (direction === 'horizontal') normalized.sort((left, right) => left.c - right.c);
      else if (direction === 'vertical') normalized.sort((left, right) => left.r - right.r);
      const fromCell = normalized[0];
      const toCell = normalized[normalized.length - 1];
      if (fromCell && toCell) {
        return {
          from: this.worldPoint(fromCell, new THREE.Vector3()),
          to: this.worldPoint(toCell, new THREE.Vector3()),
        };
      }
    }
    const center = (fallbackCenter ?? this.origin).clone();
    const basis = direction === 'vertical'
      ? this.boardDown
      : direction === 'diagonal'
        ? this.temporary.direction.copy(this.boardRight).add(this.boardDown).normalize()
        : this.boardRight;
    const span = this.cellSize * (direction === 'diagonal' ? 2.2 : 2.8);
    return {
      from: center.clone().addScaledVector(basis, -span),
      to: center.clone().addScaledVector(basis, span),
    };
  }

  private defaultPrismTargets(center: THREE.Vector3): THREE.Vector3[] {
    const distance = this.cellSize * 2.1;
    const targets: THREE.Vector3[] = [];
    const directions = [
      [1, 0],
      [0.5, 0.866],
      [-0.5, 0.866],
      [-1, 0],
      [-0.5, -0.866],
      [0.5, -0.866],
    ];
    for (const [right, down] of directions) {
      targets.push(center.clone()
        .addScaledVector(this.boardRight, right * distance)
        .addScaledVector(this.boardDown, down * distance));
    }
    return targets;
  }

  private prismColor(index: number, fallback: THREE.Color): ColorRepresentation {
    const colors: readonly ColorRepresentation[] = ['#ff8eae', '#ffd36a', '#69e0b5', '#63ddff', '#c493ff', '#ff9b71'];
    if (index === 0 && fallback) return fallback;
    return colors[index % colors.length];
  }

  private comboDirections(first: SpecialKind, second: SpecialKind): THREE.Vector3[] {
    const directions: THREE.Vector3[] = [];
    if (first === 'beamH' || second === 'beamH') directions.push(this.boardRight.clone(), this.boardRight.clone().negate());
    if (first === 'beamV' || second === 'beamV') directions.push(this.boardDown.clone(), this.boardDown.clone().negate());
    if (directions.length === 0 || first === 'nova' || second === 'nova') {
      directions.push(
        this.boardRight.clone().add(this.boardDown).normalize(),
        this.boardRight.clone().sub(this.boardDown).normalize(),
        this.boardRight.clone().addScaledVector(this.boardDown, -1).normalize(),
        this.boardRight.clone().negate().add(this.boardDown).normalize(),
      );
    }
    return directions;
  }

  private random(): number {
    let value = this.randomState;
    value ^= value << 13;
    value ^= value >>> 17;
    value ^= value << 5;
    this.randomState = value >>> 0;
    return this.randomState / 0x100000000;
  }

  private randomDirection(): THREE.Vector3 {
    const direction = new THREE.Vector3(
      this.random() * 2 - 1,
      this.random() * 2 - 1,
      this.random() * 2 - 1,
    );
    if (direction.lengthSq() < EPSILON) direction.set(0, 1, 0);
    return direction.normalize();
  }

  private scaledDuration(duration: number): number {
    const safeDuration = Math.max(0.08, Number.isFinite(duration) ? duration : 0.5);
    return this.reducedMotion ? Math.max(0.12, safeDuration * 0.58) : safeDuration;
  }

  private qualityParticleBudget(): number {
    if (this.quality === 'low') return 90;
    if (this.quality === 'medium') return 170;
    return 260;
  }
}

export default VfxDirector;
